/**
 * @file     PreApprovalContext.tsx
 * @purpose  React context + hook for opening PreApprovalModal from anywhere
 *           in the listing detail tree without prop drilling.
 */
'use client';

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { useViewedListings } from '@/hooks/useViewedListings';

export interface PreApprovalPayload {
  listingKey: string;
  listingTitle: string | null;
  listingPrice: number | null;
  /** Runs once the request is accepted, so the caller can report its own lead event. */
  onSubmitted?: () => void;
}

interface ContextValue {
  isOpen: boolean;
  payload: PreApprovalPayload | null;
  open: (payload: PreApprovalPayload) => void;
  close: () => void;
}

const PreApprovalCtx = createContext<ContextValue | null>(null);

export function PreApprovalProvider({ children }: { children: ReactNode }) {
  const [payload, setPayload] = useState<PreApprovalPayload | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { record } = useViewedListings();

  const open = useCallback(
    (p: PreApprovalPayload) => {
      record(p.listingKey);
      setPayload(p);
      setIsOpen(true);
    },
    [record],
  );

  const close = useCallback(() => setIsOpen(false), []);

  return (
    <PreApprovalCtx.Provider value={{ isOpen, payload, open, close }}>
      {children}
    </PreApprovalCtx.Provider>
  );
}

export function usePreApproval(): ContextValue {
  const ctx = useContext(PreApprovalCtx);
  if (!ctx) throw new Error('usePreApproval must be used inside PreApprovalProvider');
  return ctx;
}
